type OverflowContainer = Pick<
  Element,
  'scrollHeight' | 'clientHeight' | 'scrollWidth' | 'clientWidth'
> & {
  dataset?: Pick<DOMStringMap, 'printOverflowKind'>
}
interface OverflowDocument {
  querySelectorAll?: (
    selector: string
  ) => ArrayLike<OverflowContainer> | Iterable<OverflowContainer>
}
interface SuperTicketOverflow {
  branding: boolean
  workDetails: boolean
}

// Keep the original operational overflow signal for the Order Summary fallback.
export function getSuperTicketOverflow(
  previewDocument?: OverflowDocument | null
): SuperTicketOverflow {
  return Array.from(
    previewDocument?.querySelectorAll?.('[data-print-overflow-container]') ?? []
  ).reduce<SuperTicketOverflow>(
    (overflow, container) => {
      if (
        container.scrollHeight > container.clientHeight + 1 ||
        container.scrollWidth > container.clientWidth + 1
      ) {
        overflow[container.dataset?.printOverflowKind === 'branding' ? 'branding' : 'workDetails'] =
          true
      }
      return overflow
    },
    { branding: false, workDetails: false }
  )
}

export function hasSuperTicketOverflow(previewDocument?: OverflowDocument | null): boolean {
  const overflow = getSuperTicketOverflow(previewDocument)
  return overflow.branding || overflow.workDetails
}

export function getSuperTicketSectionOverflow(
  document: Document,
  sectionKeys?: readonly (string | undefined)[]
): (string | undefined)[] {
  const clipped: (string | undefined)[] = []
  for (const section of document.querySelectorAll<HTMLElement>('[data-section]')) {
    if (sectionKeys && !sectionKeys.includes(section.dataset.section)) continue
    const bounds = section.getBoundingClientRect()
    if (!bounds.width || !bounds.height) continue
    const outside = (rect: DOMRectReadOnly, boundary: DOMRectReadOnly): boolean =>
      rect.width > 0 &&
      rect.height > 0 &&
      (rect.top < boundary.top - 1 ||
        rect.bottom > boundary.bottom + 1 ||
        rect.left < boundary.left - 1 ||
        rect.right > boundary.right + 1)
    // Text ranges reflect the transformed ink, rather than a scaled wrapper's original scroll size.
    const walker = document.createTreeWalker(section, 4)
    let node: Node | null
    let overflow = false
    while ((node = walker.nextNode()) && !overflow) {
      // Product descriptions intentionally show two lines. Measure their visible
      // boxes below, not the hidden text that the browser keeps in text ranges.
      if (!node.textContent?.trim() || node.parentElement?.closest('.item-description')) continue
      const range = document.createRange()
      range.selectNodeContents(node)
      for (const rect of range.getClientRects()) {
        if (outside(rect, bounds)) {
          overflow = true
          break
        }
        for (
          let parent = node.parentElement;
          parent && parent !== section;
          parent = parent.parentElement
        ) {
          if (
            document.defaultView?.getComputedStyle(parent).overflow === 'hidden' &&
            outside(rect, parent.getBoundingClientRect())
          ) {
            overflow = true
            break
          }
        }
      }
    }
    overflow ||= Array.from(section.querySelectorAll('img, svg, .item-description')).some(
      (element) => outside(element.getBoundingClientRect(), bounds)
    )
    if (overflow) clipped.push(section.dataset.section)
  }
  return clipped
}
